import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import { ShippingOptionType } from '../../bff-api/baskets/model';
import { StoresInventoryStatus } from '../../bff-api/stores/model';
import type { InventoryDetails } from '../../bff-api/baskets/model/BasketAvailability';
import type { StoreInventory } from '../../bff-api/stores/model';
import type { Inventories, Inventory, InventoryFlags } from './types';

export const DEFAULT_INVENTORY: Inventory = {
    status: StoresInventoryStatus.NOT_LOADED,
    availableToSell: 0,
    allocatedStock: 0,
    backorderStock: 0,
};

export function getInventories(
    productInventory?: ShopperProductsTypes.Inventory,
    bopisInventory?: StoreInventory,
    sddInventory?: StoreInventory,
): Inventories {
    const availableToSell = productInventory?.ats ?? 0;
    const allocatedStock = productInventory?.stockLevel ?? 0;
    const backorderStock = availableToSell - allocatedStock;
    const backorderInStockDate = productInventory?.inStockDate
        ? new Date(String(productInventory.inStockDate).slice(0, -1)).getTime()
        : undefined;

    return {
        [ShippingOptionType.SHIP_TO]: productInventory
            ? {
                  status: productInventory.ats
                      ? StoresInventoryStatus.PRODUCT_AVAILABLE
                      : StoresInventoryStatus.PRODUCT_NOT_AVAILABLE,
                  availableToSell: productInventory?.ats ?? 0,
                  allocatedStock,
                  backorderStock,
                  backorderInStockDate,
              }
            : DEFAULT_INVENTORY,
        [ShippingOptionType.SDD]: sddInventory
            ? {
                  status: sddInventory?.status ?? StoresInventoryStatus.NOT_LOADED,
                  availableToSell: sddInventory?.quantityAvailable ?? 0,
                  backorderStock: 0,
                  allocatedStock: sddInventory?.quantityAvailable ?? 0,
                  store: sddInventory?.store,
              }
            : DEFAULT_INVENTORY,
        [ShippingOptionType.BOPIS]: bopisInventory
            ? {
                  status: bopisInventory?.status ?? StoresInventoryStatus.NOT_LOADED,
                  availableToSell: bopisInventory?.quantityAvailable ?? 0,
                  backorderStock: 0,
                  allocatedStock: bopisInventory?.quantityAvailable ?? 0,
                  store: bopisInventory?.store,
              }
            : DEFAULT_INVENTORY,
    };
}

interface GetFlagFnArgs {
    inventory: Map<string, InventoryDetails>;
    thresholdOnlyXLeft: number;
    thresholdLimitedQuantity: number;
}
interface GetFlagForProductArgs {
    productId: string;
    quantity: number;
}

const getFlag =
    ({ inventory, thresholdOnlyXLeft, thresholdLimitedQuantity }: GetFlagFnArgs) =>
    ({ productId, quantity }: GetFlagForProductArgs): InventoryFlags => {
        const productInventory = inventory.get(productId);

        if (!productInventory) {
            // While the inventory is not loaded, assume everything is in stock to avoid showing error messages.
            return {
                hasEnoughStock: true,
                isLimitedQuantityBadge: false,
                isOnlyXLeftBadge: false,
                includesItemsInBackorder: false,
                isOnlyBackOrder: false,
            };
        }

        const { availableToSell, allocatedStock } = productInventory;
        const includesItemsInBackorder = quantity > allocatedStock && quantity <= availableToSell;

        return {
            hasEnoughStock: quantity <= availableToSell,
            isLimitedQuantityBadge:
                availableToSell > thresholdOnlyXLeft && availableToSell <= thresholdLimitedQuantity,
            isOnlyXLeftBadge: availableToSell > 0 && availableToSell <= thresholdOnlyXLeft,
            includesItemsInBackorder,
            isOnlyBackOrder: includesItemsInBackorder && allocatedStock === 0,
        };
    };

interface GetBasketInventoryFlagsFnArgs {
    productSelection: { productId: string; quantity: number }[];
    inventory: Map<string, InventoryDetails>;
    thresholdOnlyXLeft: number;
    thresholdLimitedQuantity: number;
}

export function getBasketInventoryFlags({
    productSelection,
    inventory,
    thresholdOnlyXLeft,
    thresholdLimitedQuantity,
}: GetBasketInventoryFlagsFnArgs): Map<string, InventoryFlags> {
    const flagsFactory = getFlag({ inventory, thresholdOnlyXLeft, thresholdLimitedQuantity });
    return new Map(
        productSelection.map(({ productId, quantity }) => [
            productId,
            flagsFactory({ productId, quantity }),
        ]),
    );
}

type GetBasketInventoryFlagFnArgs = GetFlagFnArgs & GetFlagForProductArgs;

export function getBasketInventoryFlag({
    productId,
    quantity,
    inventory,
    thresholdOnlyXLeft,
    thresholdLimitedQuantity,
}: GetBasketInventoryFlagFnArgs): InventoryFlags {
    return getFlag({ inventory, thresholdOnlyXLeft, thresholdLimitedQuantity })({
        productId,
        quantity,
    });
}

interface GetOneFlagFnArgs {
    productId: string;
    quantity: number;
    currentInventory: InventoryDetails;
    shipToInventory: InventoryDetails;
    thresholdOnlyXLeft: number;
    thresholdLimitedQuantity: number;
}

export function getPDPInventoryFlags({
    productId,
    quantity,
    currentInventory,
    shipToInventory,
    thresholdOnlyXLeft,
    thresholdLimitedQuantity,
}: GetOneFlagFnArgs): InventoryFlags {
    const inventoryMap = new Map<string, InventoryDetails>([[productId, currentInventory]]);

    const flags = getFlag({
        inventory: inventoryMap,
        thresholdOnlyXLeft,
        thresholdLimitedQuantity,
    })({
        productId,
        quantity,
    });

    const { allocatedStock, availableToSell } = shipToInventory;
    const includesItemsInBackorder = quantity > allocatedStock && quantity <= availableToSell;

    return {
        ...flags,
        includesItemsInBackorder,
        isOnlyBackOrder: includesItemsInBackorder && allocatedStock === 0,
    };
}
